import { curveMonotoneX } from '@visx/curve';
import { scaleLinear } from '@visx/scale';
import { AreaClosed, LinePath } from '@visx/shape';
import { useId } from 'react';
import { cn } from '../lib/cn.ts';
import { TONE_VAR } from '../lib/status.ts';
import type { Tone } from '../lib/status.ts';

export interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
  /** Fixed scale; defaults to the data's own min and max. Use min 0, max 1 for load fractions. */
  min?: number;
  max?: number;
  tone?: Tone;
  /** A chart palette slot (data-viz.md) instead of a tone: the colour for a series that is not a status. */
  slot?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  /** Soft gradient fill under the line. */
  fill?: boolean;
  /** Dot on the latest value. */
  showLast?: boolean;
  /** Accessible summary, for example "CPU load, last 5 minutes, now 42%". */
  label: string;
  className?: string;
}

/**
 * A tiny trend line on visx (data-viz.md: inside a KPI tile, 28 px high, no axes, the last
 * point marked). Values are drawn left (oldest) to right (newest).
 */
export function Sparkline({ values, width = 96, height = 28, min, max, tone = 'accent', slot, fill = true, showLast = true, label, className }: SparklineProps) {
  const gid = `spark${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const color = slot ? `var(--data-${slot})` : TONE_VAR[tone];
  const pad = 2;
  if (values.length < 2) {
    return (
      <svg width={width} height={height} role="img" aria-label={label} className={cn('shrink-0', className)}>
        <line x1={pad} x2={width - pad} y1={height / 2} y2={height / 2} stroke="var(--border-strong)" strokeDasharray="2 3" />
      </svg>
    );
  }
  const lo = min ?? Math.min(...values);
  const hi = max ?? Math.max(...values);
  const x = scaleLinear<number>({ domain: [0, values.length - 1], range: [pad, width - pad] });
  const y = scaleLinear<number>({ domain: [lo, hi === lo ? lo + 1 : hi], range: [height - pad, pad], clamp: true });
  const last = values.length - 1;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label} className={cn('shrink-0 overflow-visible', className)}>
      {fill && (
        <>
          <defs>
            <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <AreaClosed<number> data={values} x={(_, i) => x(i)} y={(v) => y(v)} yScale={y} curve={curveMonotoneX} fill={`url(#${gid})`} />
        </>
      )}
      <LinePath<number> className="motion-draw" pathLength={1} strokeDasharray="1" data={values} x={(_, i) => x(i)} y={(v) => y(v)} curve={curveMonotoneX} stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      {showLast && <circle cx={x(last)} cy={y(values[last] ?? lo)} r="2.25" fill={color} />}
    </svg>
  );
}
